import { useState } from 'react';
import { App, Button, Card, Input, Popconfirm, Select, Space, Table, Tag, Typography, type TableColumnsType } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../../api/client';
import { errMsg } from '../../../lib/errors';
import { QueryError } from '../../../lib/QueryState';
import { Can } from '../../../rbac/RequirePermission';
import type { Holiday, HolidayKind } from './employmentTypes';

export const HOLIDAYS_QUERY_KEY = ['admin', 'employment', 'holidays'];

/**
 * Holidays that apply to every employee (global rows) and to THIS employee
 * only. A per-employee row may also be a "working day" — an exception that
 * turns a global holiday back into a normal scheduled day for one person.
 */
export function EmploymentHolidaysCard({
  employmentId,
  from,
  to,
}: {
  /** null = not an employee yet: only global holidays are listed / added. */
  employmentId: string | null;
  from: string;
  to: string;
}) {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const [date, setDate] = useState('');
  const [name, setName] = useState('');
  const [scope, setScope] = useState<'global' | 'employee'>(employmentId ? 'employee' : 'global');
  const [kind, setKind] = useState<HolidayKind>('holiday');

  const holidays = useQuery({
    queryKey: [...HOLIDAYS_QUERY_KEY, from, to, employmentId],
    queryFn: async () =>
      (await api.get<{ items: Holiday[] }>('/admin/employment/holidays', { params: { from, to, employmentId: employmentId ?? undefined } })).data,
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: HOLIDAYS_QUERY_KEY });
    // Holidays change what a day is worth.
    void qc.invalidateQueries({ queryKey: ['admin', 'employment'] });
  };

  const create = useMutation({
    mutationFn: async () =>
      (
        await api.post<Holiday>('/admin/employment/holidays', {
          holidayDate: date,
          name: name.trim(),
          employmentId: scope === 'employee' ? employmentId : null,
          kind: scope === 'employee' ? kind : 'holiday',
        })
      ).data,
    onSuccess: () => {
      message.success('Holiday added');
      setDate('');
      setName('');
      invalidate();
    },
    onError: (e: unknown) => {
      const code = (e as { response?: { data?: { code?: string } } }).response?.data?.code;
      message.error(code === 'HOLIDAY_EXISTS' ? 'There is already a row for that date.' : errMsg(e, 'Could not add the holiday'));
    },
  });

  const remove = useMutation({
    mutationFn: async (id: string) => (await api.delete<{ ok: true }>(`/admin/employment/holidays/${id}`)).data,
    onSuccess: () => {
      message.success('Holiday removed');
      invalidate();
    },
    onError: (e: unknown) => message.error(errMsg(e, 'Could not remove the holiday')),
  });

  const columns: TableColumnsType<Holiday> = [
    { title: 'Date', dataIndex: 'holidayDate', width: 120 },
    { title: 'Name', dataIndex: 'name' },
    {
      title: 'Applies to',
      width: 150,
      render: (_: unknown, h) => (h.employmentId ? <Tag color="blue">This employee</Tag> : <Tag>Everyone</Tag>),
    },
    {
      title: 'Kind',
      width: 130,
      render: (_: unknown, h) => (h.kind === 'working_day' ? <Tag color="orange">Working day</Tag> : <Tag color="green">Holiday</Tag>),
    },
    {
      title: '',
      width: 60,
      render: (_: unknown, h) => (
        <Can permission="listeners.employment.edit">
          <Popconfirm title={`Remove "${h.name}" on ${h.holidayDate}?`} onConfirm={() => remove.mutate(h.id)} okText="Remove" okButtonProps={{ danger: true }}>
            <Button size="small" danger icon={<DeleteOutlined />} aria-label={`Remove holiday ${h.name}`} />
          </Popconfirm>
        </Can>
      ),
    },
  ];

  const valid = /^\d{4}-\d{2}-\d{2}$/.test(date) && name.trim().length > 0;

  return (
    <Card
      size="small"
      title="Holidays"
      extra={<Typography.Text type="secondary">{from} – {to} · global holidays and this employee&apos;s own</Typography.Text>}
      style={{ marginBottom: 12 }}
    >
      <Can permission="listeners.employment.edit">
        <Space wrap style={{ marginBottom: 12 }}>
          <Input type="date" aria-label="Holiday date" value={date} onChange={(e) => setDate(e.target.value)} style={{ width: 160 }} />
          <Input aria-label="Holiday name" placeholder="Name, e.g. Pongal" value={name} maxLength={80} onChange={(e) => setName(e.target.value)} style={{ width: 200 }} />
          <Select<'global' | 'employee'>
            aria-label="Holiday applies to"
            value={scope}
            onChange={(v) => {
              setScope(v);
              if (v === 'global') setKind('holiday');
            }}
            options={[
              { value: 'global', label: 'Everyone' },
              { value: 'employee', label: 'This employee only', disabled: !employmentId },
            ]}
            style={{ width: 180 }}
          />
          <Select<HolidayKind>
            aria-label="Holiday kind"
            value={kind}
            disabled={scope === 'global'}
            onChange={setKind}
            options={[
              { value: 'holiday', label: 'Holiday' },
              { value: 'working_day', label: 'Working day (overrides a holiday)' },
            ]}
            style={{ width: 250 }}
          />
          <Button type="primary" icon={<PlusOutlined />} disabled={!valid} loading={create.isPending} onClick={() => create.mutate()}>
            Add
          </Button>
        </Space>
      </Can>
      {holidays.isError ? (
        <QueryError error={holidays.error} what="holidays" onRetry={() => void holidays.refetch()} />
      ) : (
        <Table<Holiday>
          size="small"
          rowKey="id"
          loading={holidays.isLoading}
          columns={columns}
          dataSource={holidays.data?.items ?? []}
          pagination={false}
          locale={{ emptyText: 'No holidays in this range' }}
        />
      )}
    </Card>
  );
}
